import { IEventListItem } from '@gem/call/CallController/CallController.types';
import { Call } from '@gem/call/CallStore/Call';
import { CallsServerErrorCode } from '@gem/call/Model/callModel.types';
import * as callPermissionsModel from '@gem/call/Model/callPermissions/callPermissionsModel';
import {
  ICallParticipantIdToRole,
  ICallPermissions,
  ICallPersonalizedPermissions,
  PermissionRequestAccess,
  PermissionsRequestStatuses,
} from '@gem/call/Model/callPermissions/callPermissionsModel.types';
import { CallEventTypes } from '@gem/call/constants';

type TAddEvent = (event: IEventListItem) => void;

export class CallPermissionsService {
  call: Call;
  addEvent: TAddEvent;

  constructor(params: { call: Call; addEvent: TAddEvent }) {
    this.call = params.call;
    this.addEvent = params.addEvent;
  }

  async getCallPermissions(channelId: string): Promise<void> {
    const response = await callPermissionsModel.getCallPermissions(channelId);

    this.call.permissions.setPermissions(response);
  }

  async setCallPermissionsForAll(
    channelId: string,
    permissions: Required<ICallPermissions>
  ): Promise<void> {
    await callPermissionsModel.setCallPermissionsForAll(channelId, permissions);
  }

  async setCallPermissionsForUser(
    channelId: string,
    userId: string,
    permissions: ICallPersonalizedPermissions
  ): Promise<void> {
    await callPermissionsModel.setCallPermissionsForUser(channelId, userId, permissions);
  }

  async clearCallPermissionsForUser(channelId: string, userId: string): Promise<void> {
    await callPermissionsModel.clearCallPermissionsForUser(channelId, userId);
  }

  async updatePermissionRequestsForAll(params: {
    channelId: string;
    status: PermissionsRequestStatuses;
  }): Promise<void> {
    await callPermissionsModel.updatePermissionRequestsForAll(params);
  }

  async updatePermissionRequestForUser(params: {
    channelId: string;
    userId: string;
    access: PermissionRequestAccess;
    status: PermissionsRequestStatuses;
  }): Promise<void> {
    await callPermissionsModel.updatePermissionRequestForUser(params);
  }

  async createPermissionRequest(params: {
    channelId: string;
    access: PermissionRequestAccess;
  }): Promise<any> {
    try {
      const response = await callPermissionsModel.createPermissionRequest(params);

      this.addEvent({
        type: CallEventTypes.PermissionRequestSent,
      });

      return response;
    } catch (error) {
      if ('message' in error && error.message === CallsServerErrorCode.PermissionAlreadySentError) {
        this.addEvent({
          type: CallEventTypes.PermissionRequestAlreadySent,
        });
      }
      return null;
    }
  }

  async getPermissionRequests(channelId: string): Promise<void> {
    try {
      const response = await callPermissionsModel.getCallPermissionsRequests(channelId);

      this.call.permissions.setPermissionsRequests(response);
    } catch {
      return;
    }
  }

  async setCallParticipantsRoles(
    channelId: string,
    participantIdsToRole: ICallParticipantIdToRole[]
  ): Promise<void> {
    await callPermissionsModel.setCallParticipantsRoles(channelId, participantIdsToRole);
  }
}
